import StudioAnalyticsAPI from 'dashboard/api/studio/analytics';

export const types = {
  SET_UI_FLAG: 'SET_STUDIO_ANALYTICS_UI_FLAG',
  SET_RECORDS: 'SET_STUDIO_ANALYTICS_RECORDS',
  SET_META: 'SET_STUDIO_ANALYTICS_META',
  SET_CURRENT: 'SET_STUDIO_ANALYTICS_CURRENT',
  SET_SUMMARY: 'SET_STUDIO_ANALYTICS_SUMMARY',
  SET_BY_MODEL: 'SET_STUDIO_ANALYTICS_BY_MODEL',
  SET_BY_AGENT: 'SET_STUDIO_ANALYTICS_BY_AGENT',
  SET_OVER_TIME: 'SET_STUDIO_ANALYTICS_OVER_TIME',
  SET_BY_CHANNEL: 'SET_STUDIO_ANALYTICS_BY_CHANNEL',
  SET_BY_TERMINATION: 'SET_STUDIO_ANALYTICS_BY_TERMINATION',
  SET_FILTERS: 'SET_STUDIO_ANALYTICS_FILTERS',
  UPSERT_SESSION: 'UPSERT_STUDIO_ANALYTICS_SESSION',
  SET_ACTIVE: 'SET_STUDIO_ANALYTICS_ACTIVE',
};

// Refresh dos agregados (summary + byModel) disparado por evento realtime:
// throttle com leading + trailing e sem empilhar lote em voo — mesmo desenho
// do Painel de Gestão (operationsDashboard.realtimeNudge). Incidente de prod
// 2026-09-29: sem isso, TODO turno de IA fazia TODA aba aberta de admin
// refazer os agregados (o handler é global, rodava com o Analytics fechado) e
// o primary do Postgres saturou.
export const AGGREGATES_REFRESH_MS = 10000;

const throttle = { timer: null, lastAt: 0 };

// Usado pelo deactivate (página desmontou) e pelos specs — timer pendente de
// uma página já fechada não pode disparar request.
export const resetAggregatesThrottle = () => {
  clearTimeout(throttle.timer);
  throttle.timer = null;
  throttle.lastAt = 0;
};

const initialFilters = () => ({
  channel: null,
  status: null,
  // 'contained' | 'not_contained' — o mesmo `contained_by_ai` do card de
  // Contenção, agora como recorte da lista.
  containment: null,
  agentId: null,
  // Número da conversa como a tela mostra (display_id), não o id interno.
  conversationDisplayId: null,
  from: null,
  to: null,
  page: 1,
  perPage: 25,
});

const state = {
  // Página de Analytics montada. Fora dela, evento realtime não busca nada:
  // a página recarrega tudo quando abre.
  active: false,
  records: [],
  meta: { totalCount: 0, page: 1, perPage: 25, totalPages: 0 },
  currentSession: null,
  summary: null,
  byModel: [],
  byAgent: [],
  overTime: [],
  byChannel: [],
  byTermination: [],
  uiFlags: {
    fetchingList: false,
    fetchingItem: false,
    fetchingSummary: false,
    fetchingByModel: false,
    fetchingByAgent: false,
    fetchingOverTime: false,
    fetchingByChannel: false,
    fetchingByTermination: false,
  },
  filters: initialFilters(),
};

const getters = {
  getRecords: $state => $state.records,
  getMeta: $state => $state.meta,
  getCurrentSession: $state => $state.currentSession,
  getSummary: $state => $state.summary,
  getByModel: $state => $state.byModel,
  getByAgent: $state => $state.byAgent,
  getOverTime: $state => $state.overTime,
  getByChannel: $state => $state.byChannel,
  getByTermination: $state => $state.byTermination,
  getUIFlags: $state => $state.uiFlags,
  getFilters: $state => $state.filters,
  isActive: $state => $state.active,
};

const buildParams = filters => {
  const params = {};
  if (filters.channel) params.channel = filters.channel;
  if (filters.status) params.status = filters.status;
  if (filters.containment) params.containment = filters.containment;
  if (filters.agentId) params.agent_id = filters.agentId;
  if (filters.conversationDisplayId)
    params.conversation_display_id = filters.conversationDisplayId;
  if (filters.from) params.from = filters.from;
  if (filters.to) params.to = filters.to;
  if (filters.page) params.page = filters.page;
  if (filters.perPage) params.per_page = filters.perPage;
  return params;
};

const actions = {
  async fetchSessions({ commit, state: $state }) {
    commit(types.SET_UI_FLAG, { fetchingList: true });
    try {
      const { data } = await StudioAnalyticsAPI.list(
        buildParams($state.filters)
      );
      commit(types.SET_RECORDS, data.data || []);
      commit(types.SET_META, data.meta || {});
    } finally {
      commit(types.SET_UI_FLAG, { fetchingList: false });
    }
  },

  async fetchSession({ commit }, sessionId) {
    commit(types.SET_UI_FLAG, { fetchingItem: true });
    try {
      const { data } = await StudioAnalyticsAPI.show(sessionId);
      commit(types.SET_CURRENT, data);
    } finally {
      commit(types.SET_UI_FLAG, { fetchingItem: false });
    }
  },

  async fetchSummary({ commit, state: $state }) {
    commit(types.SET_UI_FLAG, { fetchingSummary: true });
    try {
      // Os cards seguem TAMBÉM os filtros da lista (status, desfecho, busca):
      // é o que responde "quanto me custaram as que precisaram de humano?".
      // Só `page`/`perPage` ficam de fora — paginar não muda a população.
      const { page: _page, perPage: _perPage, ...scope } = $state.filters;
      const { data } = await StudioAnalyticsAPI.summary(buildParams(scope));
      commit(types.SET_SUMMARY, data);
    } finally {
      commit(types.SET_UI_FLAG, { fetchingSummary: false });
    }
  },

  async fetchByModel({ commit, state: $state }) {
    commit(types.SET_UI_FLAG, { fetchingByModel: true });
    try {
      const { from, to, channel } = $state.filters;
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      if (channel) params.channel = channel;
      const { data } = await StudioAnalyticsAPI.byModel(params);
      commit(types.SET_BY_MODEL, data.data || []);
    } finally {
      commit(types.SET_UI_FLAG, { fetchingByModel: false });
    }
  },

  async fetchByAgent({ commit, state: $state }) {
    commit(types.SET_UI_FLAG, { fetchingByAgent: true });
    try {
      const { from, to, channel } = $state.filters;
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      if (channel) params.channel = channel;
      const { data } = await StudioAnalyticsAPI.byAgent(params);
      commit(types.SET_BY_AGENT, data.data || []);
    } finally {
      commit(types.SET_UI_FLAG, { fetchingByAgent: false });
    }
  },

  async fetchOverTime({ commit, state: $state }) {
    commit(types.SET_UI_FLAG, { fetchingOverTime: true });
    try {
      const { from, to, channel } = $state.filters;
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      if (channel) params.channel = channel;
      const { data } = await StudioAnalyticsAPI.overTime(params);
      commit(types.SET_OVER_TIME, data.data || []);
    } finally {
      commit(types.SET_UI_FLAG, { fetchingOverTime: false });
    }
  },

  async fetchByChannel({ commit, state: $state }) {
    commit(types.SET_UI_FLAG, { fetchingByChannel: true });
    try {
      const { from, to } = $state.filters;
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      const { data } = await StudioAnalyticsAPI.byChannel(params);
      commit(types.SET_BY_CHANNEL, data.data || []);
    } finally {
      commit(types.SET_UI_FLAG, { fetchingByChannel: false });
    }
  },

  async fetchByTermination({ commit, state: $state }) {
    commit(types.SET_UI_FLAG, { fetchingByTermination: true });
    try {
      const { from, to, channel } = $state.filters;
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      if (channel) params.channel = channel;
      const { data } = await StudioAnalyticsAPI.byTermination(params);
      commit(types.SET_BY_TERMINATION, data.data || []);
    } finally {
      commit(types.SET_UI_FLAG, { fetchingByTermination: false });
    }
  },

  setFilters({ commit }, partial) {
    commit(types.SET_FILTERS, partial);
  },

  resetFilters({ commit }) {
    commit(types.SET_FILTERS, initialFilters());
  },

  activate({ commit }) {
    commit(types.SET_ACTIVE, true);
  },

  deactivate({ commit }) {
    resetAggregatesThrottle();
    commit(types.SET_ACTIVE, false);
  },

  // Recebe broadcast ActionCable em ai.session.event. Payload:
  //   { event_type: 'session.upserted'|'session.completed'|'complaint.added',
  //     account_id, session: { ...payload completo da Ai::Session... } }
  // Com a página aberta: substitui/insere a row e refaz os agregados (KPIs
  // precisam recalcular), com throttle. Fechada: só o detalhe da sessão aberto
  // (rota própria, fora do invólucro do Analytics) acompanha o evento.
  handleRealtimeEvent({ commit, dispatch, state: $state }, payload) {
    const eventType = payload?.event_type;
    const session = payload?.session;
    if (!session?.id) return;

    if ($state.currentSession?.id === session.id) {
      commit(types.SET_CURRENT, { ...$state.currentSession, ...session });
    }

    if (!$state.active) return;

    // Insere/atualiza no records se houver match com filtros atuais ou
    // se a sessao ja estava na lista. UPSERT_SESSION decide.
    commit(types.UPSERT_SESSION, session);

    if (
      eventType === 'session.completed' ||
      eventType === 'session.upserted' ||
      eventType === 'complaint.added'
    ) {
      dispatch('refreshAggregates');
    }
  },

  refreshAggregates({ state: $state, dispatch }) {
    if (!$state.active) return;

    const elapsed = Date.now() - throttle.lastAt;
    // Lote ainda no ar = NÃO empilha outro por cima: o throttle espaça os
    // inícios, mas quem protege o backend é esperar o lote anterior TERMINAR.
    const busy =
      $state.uiFlags.fetchingSummary || $state.uiFlags.fetchingByModel;

    if (!busy && elapsed >= AGGREGATES_REFRESH_MS) {
      throttle.lastAt = Date.now();
      dispatch('fetchSummary');
      dispatch('fetchByModel');
      return;
    }

    if (throttle.timer) return;

    // O timer re-despacha o próprio refresh (não o fetch): na hora de
    // disparar, os guards — página montada, lote em voo — são reavaliados.
    const delay = busy
      ? AGGREGATES_REFRESH_MS
      : AGGREGATES_REFRESH_MS - elapsed;
    throttle.timer = setTimeout(() => {
      throttle.timer = null;
      dispatch('refreshAggregates');
    }, delay);
  },
};

const mutations = {
  [types.SET_UI_FLAG]($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
  [types.SET_RECORDS]($state, records) {
    $state.records = records;
  },
  [types.SET_META]($state, meta) {
    $state.meta = {
      totalCount: Number(meta.total_count || 0),
      page: Number(meta.page || 1),
      perPage: Number(meta.per_page || 25),
      totalPages: Number(meta.total_pages || 0),
    };
  },
  [types.SET_CURRENT]($state, session) {
    $state.currentSession = session;
  },
  [types.SET_SUMMARY]($state, summary) {
    $state.summary = summary;
  },
  [types.SET_BY_MODEL]($state, byModel) {
    $state.byModel = byModel;
  },
  [types.SET_BY_AGENT]($state, byAgent) {
    $state.byAgent = byAgent;
  },
  [types.SET_OVER_TIME]($state, overTime) {
    $state.overTime = overTime;
  },
  [types.SET_BY_CHANNEL]($state, byChannel) {
    $state.byChannel = byChannel;
  },
  [types.SET_BY_TERMINATION]($state, byTermination) {
    $state.byTermination = byTermination;
  },
  [types.SET_ACTIVE]($state, active) {
    $state.active = active;
  },
  [types.SET_FILTERS]($state, partial) {
    $state.filters = { ...$state.filters, ...partial };
  },
  [types.UPSERT_SESSION]($state, session) {
    const idx = $state.records.findIndex(r => r.id === session.id);
    if (idx >= 0) {
      // Substitui a REFERÊNCIA do array (não splice in-place). tanstack/vue-table
      // cacheia getSortedRowModel pela ref; splice mantém a mesma ref e a tabela
      // não rerenderiza colunas (status, etc) até refetch. Vuex/Vue 3 detectam o
      // splice, mas o cache da lib não.
      $state.records = $state.records.map((r, i) =>
        i === idx ? { ...r, ...session } : r
      );
    } else {
      // Sessão nova — prepend (mais recente em cima, ordem `started_at desc`).
      // Se filtro atual exclui, o próximo refresh do server vai limpar.
      $state.records = [session, ...$state.records];
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
